import { useState, type FormEvent } from "react";
import { useAuth } from "@clerk/react";
import { cn } from "@/lib/utils";
import { authenticatedApi, ApiRequestError } from "@/lib/api";
import { formatLocalDate, startOfWeekIso } from "../../../../shared/local-date";
import {
  CURRENT_FORM_VERSION,
  getFormEnumFields,
  getFormFields,
  variantLabel,
  type FormFieldDef,
  type FormTone,
} from "../../../../shared/form-registry";
import {
  FORM_VARIANTS,
  type FormNarrativeKey,
  type FormSnapshotData,
  type FormVariant,
} from "../../../../shared/form-types";
import { useFormDirtyGuard } from "./use-form-dirty-guard";
import { DiscardChangesDialog } from "./discard-changes-dialog";

const CARD =
  "overflow-hidden rounded-[14px] border border-sand-200 bg-card shadow-[0_1px_2px_rgba(40,3,15,0.05)]";
const CARD_HEAD =
  "flex min-h-[48px] items-center justify-between gap-3 border-b border-sand-200 bg-sand-100 px-4 py-2";
const CARD_TITLE =
  "font-sans text-[12px] font-extrabold uppercase tracking-[0.06em] text-foreground";
const F_LABEL =
  "mb-1.5 mt-4 font-sans text-[10.5px] font-extrabold uppercase tracking-[0.08em] text-sand-500";
const F_INPUT =
  "block w-full min-h-[38px] rounded-[10px] border border-sand-200 bg-card px-3 py-2 text-[13.5px] leading-[1.5] text-foreground placeholder:text-sand-300 outline-none focus:border-sand-300";

const NARRATIVE_KEYS: FormNarrativeKey[] = [
  "biggestWin",
  "biggestBarrier",
  "coachingFocus",
  "actionPlan",
];

const ON_TONE: Record<FormTone, string> = {
  quiet: "border-sand-600 bg-sand-600 text-white",
  warn: "border-sunlight-500 bg-sunlight-100 text-sunlight-500",
  risk: "border-orange-500 bg-orange-100 text-orange-500",
};

type EnumKey =
  | "status"
  | "trajectory"
  | "overallConfidence"
  | "counselingDevelopment"
  | "clinicalDirectorAttention";

type EnumState = Record<EnumKey, string | null>;
type NarrativeState = Record<FormNarrativeKey, string>;

function initialEnums(snapshot?: FormSnapshotData): EnumState {
  return {
    status: snapshot?.status ?? null,
    trajectory: snapshot?.trajectory ?? null,
    overallConfidence: snapshot?.overallConfidence ?? null,
    counselingDevelopment: snapshot?.counselingDevelopment ?? null,
    clinicalDirectorAttention: snapshot?.clinicalDirectorAttention ?? null,
  };
}

function initialNarrative(snapshot?: FormSnapshotData): NarrativeState {
  return {
    biggestWin: snapshot?.response.biggestWin ?? "",
    biggestBarrier: snapshot?.response.biggestBarrier ?? "",
    coachingFocus: snapshot?.response.coachingFocus ?? "",
    actionPlan: snapshot?.response.actionPlan ?? "",
  };
}

interface WeeklyFormProps {
  rdId: string;
  rdName: string;
  rdSupervisorUserId: string | null;
  rdSupervisorName: string | null;
  /** Default variant for a fresh form (from the RD's support track). */
  defaultVariant: FormVariant;
  /**
   * Present => backfill mode: the form captures one of these past week-starts
   * (already filtered to weeks without a weekly form) instead of the current
   * week. Ignored when editing.
   */
  backfillWeeks?: string[];
  /** Present => edit an existing snapshot (keeps its variant + version). */
  snapshot?: FormSnapshotData;
  onSuccess: () => void;
  onClose: () => void;
}

export function WeeklyForm({
  rdId,
  rdName,
  rdSupervisorUserId,
  rdSupervisorName,
  defaultVariant,
  backfillWeeks,
  snapshot,
  onSuccess,
  onClose,
}: WeeklyFormProps) {
  const { getToken } = useAuth();
  const isEdit = snapshot !== undefined;
  const isBackfill = !isEdit && (backfillWeeks?.length ?? 0) > 0;
  const formVersion = snapshot?.formVersion ?? CURRENT_FORM_VERSION;

  const [variant, setVariant] = useState<FormVariant>(
    snapshot?.formVariant ?? defaultVariant,
  );
  const [enums, setEnums] = useState<EnumState>(() => initialEnums(snapshot));
  const [narrative, setNarrative] = useState<NarrativeState>(() =>
    initialNarrative(snapshot),
  );
  const [showVariantPicker, setShowVariantPicker] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initialPeriodStart =
    snapshot?.periodStartDate ?? backfillWeeks?.[0] ?? startOfWeekIso();
  const [periodStart, setPeriodStart] = useState(initialPeriodStart);
  const supervisorName = isEdit
    ? snapshot.supervisorName
    : (rdSupervisorName ?? "—");

  const fields = getFormFields("weekly_form", variant, formVersion);
  const enumFields = getFormEnumFields("weekly_form", variant, formVersion);

  const [savedSnapshot, setSavedSnapshot] = useState(() =>
    JSON.stringify({
      variant: snapshot?.formVariant ?? defaultVariant,
      periodStart: initialPeriodStart,
      enums: initialEnums(snapshot),
      narrative: initialNarrative(snapshot),
    }),
  );
  const currentSnapshot = JSON.stringify({
    variant,
    periodStart,
    enums,
    narrative,
  });
  const isDirty = currentSnapshot !== savedSnapshot;
  const guard = useFormDirtyGuard(isDirty);

  const missingRequired = enumFields.some(
    (field) => field.required && !enums[field.key as EnumKey],
  );
  const canSave = !missingRequired && !isSubmitting;

  function selectEnum(key: EnumKey, value: string) {
    setEnums((prev) => ({ ...prev, [key]: value }));
  }

  function changeVariant(next: FormVariant) {
    setVariant(next);
    setShowVariantPicker(false);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSave) return;

    setIsSubmitting(true);
    setError(null);

    const enumBody: Record<string, string | null> = {};
    for (const field of enumFields) {
      enumBody[field.key] = enums[field.key as EnumKey];
    }
    const narrativeBody: Record<string, string | null> = {};
    for (const key of NARRATIVE_KEYS) {
      narrativeBody[key] = narrative[key].trim() || null;
    }

    try {
      const endpoint = isEdit
        ? `/api/rds/${rdId}/forms/${snapshot.id}`
        : `/api/rds/${rdId}/forms`;
      const body: Record<string, unknown> = {
        formVariant: variant,
        ...enumBody,
        ...narrativeBody,
      };
      if (!isEdit) {
        body.formType = "weekly_form";
        if (rdSupervisorUserId) body.supervisorUserId = rdSupervisorUserId;
        if (isBackfill) body.periodStartDate = periodStart;
      }

      await authenticatedApi(getToken, endpoint, {
        method: isEdit ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      setSavedSnapshot(currentSnapshot);
      onSuccess();
    } catch (submitError) {
      if (submitError instanceof ApiRequestError) {
        const errBody = submitError.body as { error?: string } | undefined;
        setError(errBody?.error ?? "Failed to save weekly form.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className={CARD}>
      <div className={CARD_HEAD}>
        <h3 className={CARD_TITLE}>
          {isEdit ? "Edit" : "New"} weekly form — week of{" "}
          {isBackfill ? (
            <select
              value={periodStart}
              disabled={isSubmitting}
              onChange={(e) => setPeriodStart(e.target.value)}
              aria-label="Week to backfill"
              className="rounded-lg border border-sand-200 bg-card px-2 py-1 font-sans text-[12px] font-semibold normal-case tracking-normal text-foreground"
            >
              {backfillWeeks?.map((week) => (
                <option key={week} value={week}>
                  {formatLocalDate(week, "shortWithYear")}
                </option>
              ))}
            </select>
          ) : (
            formatLocalDate(periodStart, "short")
          )}
        </h3>
        <span className="whitespace-nowrap text-[12px] text-sand-400">
          Supervisor: {supervisorName}
        </span>
      </div>

      <div className="px-4 pb-4 pt-3">
        {/* Variant line — set by support track, overridable */}
        <div className="flex flex-wrap items-baseline gap-2 pb-1 pt-2.5 text-[12.5px] text-sand-500">
          <strong className="font-[650] text-sand-600">
            {variantLabel(variant)} question set
          </strong>
          {!isEdit && (
            <span>
              from the support track in {rdName}&rsquo;s profile
            </span>
          )}
          {showVariantPicker ? (
            <span
              className="inline-flex flex-wrap gap-1.5"
              role="radiogroup"
              aria-label="Change form variant"
            >
              {FORM_VARIANTS.map((v) => {
                const selected = v === variant;
                return (
                  <button
                    key={v}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => changeVariant(v)}
                    className={cn(
                      "min-h-[26px] rounded-full border px-3 text-[12px] font-semibold",
                      selected
                        ? "border-sand-600 bg-sand-600 text-white"
                        : "border-sand-200 bg-card text-sand-500 hover:border-sand-300 hover:text-foreground",
                    )}
                  >
                    {variantLabel(v)}
                  </button>
                );
              })}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setShowVariantPicker(true)}
              className="text-[12px] font-semibold text-interactive"
            >
              Change
            </button>
          )}
        </div>

        {fields.map((field) => (
          <FormField
            key={field.key}
            field={field}
            enumValue={
              field.kind === "enum" ? enums[field.key as EnumKey] : null
            }
            textValue={
              field.kind === "enum"
                ? ""
                : narrative[field.key as FormNarrativeKey]
            }
            disabled={isSubmitting}
            onSelectEnum={(value) => selectEnum(field.key as EnumKey, value)}
            onChangeText={(value) =>
              setNarrative((prev) => ({
                ...prev,
                [field.key as FormNarrativeKey]: value,
              }))
            }
          />
        ))}

        {error && <p className="mt-4 text-sm text-error">{error}</p>}

        <div className="mt-5 flex justify-end gap-2 border-t border-sand-200 pt-3.5">
          <button
            type="button"
            onClick={() => guard.requestClose(onClose)}
            disabled={isSubmitting}
            className="inline-flex min-h-[34px] items-center rounded-lg border border-sand-200 bg-card px-4 text-[13px] font-semibold text-foreground shadow-sm hover:bg-sand-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSave}
            className="inline-flex min-h-[34px] items-center rounded-lg border border-interactive bg-interactive px-4 text-[13px] font-semibold text-white shadow-sm hover:bg-interactive-hover disabled:opacity-50"
          >
            {isSubmitting ? "Saving…" : "Save weekly form"}
          </button>
        </div>
      </div>

      <DiscardChangesDialog
        open={guard.showDiscardDialog}
        onOpenChange={guard.handleDiscardDialogOpenChange}
        onDiscard={(e) => {
          e.preventDefault();
          guard.confirmDiscard(onClose);
        }}
        disabled={isSubmitting}
        description="This weekly form has edits that haven't been saved. If you leave now, those changes will be lost."
      />
    </form>
  );
}

function FormField({
  field,
  enumValue,
  textValue,
  disabled,
  onSelectEnum,
  onChangeText,
}: {
  field: FormFieldDef;
  enumValue: string | null;
  textValue: string;
  disabled: boolean;
  onSelectEnum: (value: string) => void;
  onChangeText: (value: string) => void;
}) {
  if (field.kind === "enum") {
    return (
      <div>
        <p className={F_LABEL}>{field.label}</p>
        <div
          className="flex flex-wrap gap-2.5 gap-y-1.5"
          role="radiogroup"
          aria-label={field.label}
        >
          {field.options?.map((option) => {
            const selected = enumValue === option.value;
            const tone = field.toneFor?.(option.value) ?? "quiet";
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={disabled}
                onClick={() => onSelectEnum(option.value)}
                className={cn(
                  "min-h-[30px] rounded-full border px-3 text-[12.5px] font-semibold transition-colors disabled:opacity-50",
                  selected
                    ? ON_TONE[tone]
                    : "border-sand-200 bg-card text-sand-500 hover:border-sand-300 hover:text-foreground",
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const inputId = `weekly-form-${field.key}`;
  return (
    <div>
      <label htmlFor={inputId} className={cn(F_LABEL, "block")}>
        {field.label}
        {field.hint && (
          <span className="ml-1 font-body text-[11.5px] font-medium normal-case tracking-normal text-sand-400">
            — {field.hint}
          </span>
        )}
      </label>
      {field.kind === "textarea" ? (
        <textarea
          id={inputId}
          value={textValue}
          disabled={disabled}
          onChange={(e) => onChangeText(e.target.value)}
          className={cn(F_INPUT, "min-h-[60px] resize-y")}
        />
      ) : (
        <input
          id={inputId}
          value={textValue}
          disabled={disabled}
          onChange={(e) => onChangeText(e.target.value)}
          className={F_INPUT}
        />
      )}
    </div>
  );
}
